import { bridgeUserErrorStates } from "../bridge/errors.js";
import { powensUserErrorStates } from "../powens/errors.js";
import type { Account } from "./models.js";

export type AccountState = {
  status:
    | "synced"
    | "pending"
    | "refused"
    | "error"
    | "reconnectionNeeded"
    | "missingBankAccountId"
    | "kycReadyForAdminReview";
  infoForAdmin?: string;
};

export const getAccountState = (account: Account): AccountState => {
  switch (account.accountType) {
    case "powens": {
      if (
        account.powens_connection?.state === null &&
        account.bankAccountId &&
        !account.powens_connection.deleted
      )
        return { status: "synced" };
      else if (!account.powens_connection) {
        return { status: "pending" };
      } else if (!account.bankAccountId) {
        return { status: "missingBankAccountId" };
      } else if (
        account.powens_connection.state &&
        powensUserErrorStates.includes(account.powens_connection.state)
      ) {
        return {
          status: "reconnectionNeeded",
          infoForAdmin: `Code: ${account.powens_connection.state}, Info: ${account.powens_connection.error_message ?? "N/A"}`,
        };
      } else if (account.powens_connection.deleted) {
        return { status: "error", infoForAdmin: "Connection deleted" };
      } else if (account.powens_connection.state) {
        return {
          status: "error",
          infoForAdmin: `Code: ${account.powens_connection.state}, Info: ${account.powens_connection.error_message ?? "N/A"}`,
        };
      } else return { status: "error", infoForAdmin: "Unknown status" };
    }
    case "bridge": {
      if (account.bridge_item?.status_code === 0 && account.bankAccountId)
        return { status: "synced" };
      else if (!account.bridge_item) {
        return { status: "pending" };
      } else if (!account.bankAccountId) {
        return { status: "missingBankAccountId" };
      } else if (
        account.bridge_item.status_code &&
        bridgeUserErrorStates.includes(account.bridge_item.status_code)
      ) {
        return {
          status: "reconnectionNeeded",
          infoForAdmin: `Code: ${account.bridge_item.status_code.toString()}, Info: ${account.bridge_item.status_code_info ?? "N/A"}`,
        };
      } else if (account.bridge_item.status_code) {
        return {
          status: "error",
          infoForAdmin: `Status: ${account.bridge_item.status_code_info ?? "N/A"}`,
        };
      } else return { status: "error", infoForAdmin: "Unknown status" };
    }
    case "gcl": {
      if (!account.gcl_account_status) {
        return { status: "pending" };
      } else if (!account.bankAccountId) {
        return { status: "missingBankAccountId" };
      } else if (account.gcl_account_status === "ERROR") {
        return { status: "error", infoForAdmin: `Status: ${account.gcl_account_status}` };
      } else if (
        account.gcl_account_status === "EXPIRED" ||
        account.gcl_account_status === "SUSPENDED"
      ) {
        return {
          status: "reconnectionNeeded",
          infoForAdmin: `Status: ${account.gcl_account_status}`,
        };
      } else {
        return { status: "synced" };
      }
    }
    case "swan": {
      if (account.swan_account_holder?.verification_status === "Refused") {
        return { status: "refused" };
      } else if (!account.swan_account || !account.swan_account.IBAN) {
        return { status: "pending" };
      } else if (account.swan_account.status === "Opened" && account.swan_account.IBAN) {
        return { status: "synced" };
      } else if (account.swan_account.status) {
        return { status: "error", infoForAdmin: `Status: ${account.swan_account.status}` };
      } else return { status: "error", infoForAdmin: "Unknown status" };
    }
    case "treezor": {
      if (account.kycAdminReview?.status === "refused") {
        return {
          status: "refused",
          infoForAdmin: `KYC refused by admin: ${account.kycAdminReview.reason}`,
        };
      } else if (
        account.treezorCompany?.userStatus === "VALIDATED" &&
        account.treezorWallet?.walletStatus === "VALIDATED" &&
        account.treezorCompany?.kyc?.kycStatus === "VALIDATED"
      ) {
        return { status: "synced" };
      } else if (account.treezorCompany?.userStatus === "CANCELED") {
        return {
          status: "error",
          infoForAdmin: account.treezorCompany.informationStatus || undefined,
        };
      } else if (account.treezorWallet?.walletStatus === "CANCELED") {
        return {
          status: "error",
          infoForAdmin: account.treezorWallet.informationStatus || undefined,
        };
      } else if (
        account.kycAdminReview?.status === "pending" &&
        (!account.treezorCompany?.kyc || account.treezorCompany.kyc.kycStatus === "NONE")
      ) {
        return {
          status: "kycReadyForAdminReview",
        };
      } else if (
        account.treezorCompany?.kyc?.kycStatus === "PENDING" ||
        account.treezorCompany?.kyc?.kycStatus === "INCOMPLETE" ||
        account.treezorCompany?.kyc?.kycStatus === "REVIEW NEEDED" ||
        account.treezorCompany?.kyc?.kycStatus === "REFUSED" ||
        account.treezorCompany?.kyc?.kycStatus === "CANCELED"
      ) {
        return {
          status: "pending",
          infoForAdmin:
            account.treezorCompany.kyc.kycReviewComment ||
            account.treezorCompany.kyc.informationStatus ||
            undefined,
        };
      } else if (account.treezorCompany?.userStatus === "PENDING") {
        return {
          status: "pending",
          infoForAdmin: account.treezorCompany.informationStatus || undefined,
        };
      } else if (account.treezorWallet?.walletStatus === "PENDING") {
        return {
          status: "pending",
          infoForAdmin: account.treezorWallet.informationStatus || undefined,
        };
      }
      return {
        status: "pending",
      };
    }
  }
};

export const isOpeningFormSent = (account: Account) => {
  switch (account.accountType) {
    case "swan":
      return account.swan_onboarding?.status === "Finalized";
    case "treezor":
      return (
        account.kycAdminReview?.status === "pending" ||
        account.kycAdminReview?.status === "validated"
      );
    case "bridge":
    case "powens":
    case "gcl":
      return false;
  }
};

/** Lower number = higher priority in account list sorting (needs attention first). */
export const getAccountStatusSortPriority = (account: Account) => {
  const { status } = getAccountState(account);
  switch (status) {
    case "kycReadyForAdminReview":
      return 1;
    case "reconnectionNeeded":
      return (
        2 -
        // order by last status updated at truncated to the day, from oldest to newest
        (account.lastStatus
          ? 1 / Math.max(1, Math.floor(account.lastStatus.updatedAt.seconds / 24 / 60 / 60))
          : 0)
      );
    case "error":
      return 3;
    case "missingBankAccountId":
      return 4;
    case "pending":
      return 5;
    case "synced":
      return 6;
    case "refused":
      return 7;
  }
};
